import React, { useCallback } from 'react';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Heart } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { clsx } from 'clsx';
import { defineMessages, useIntl } from 'react-intl';
import { TemplateSummary } from '../../models';
import { useFavoriteTemplatingTemplates } from '../../hooks/useFavoriteTemplatingTemplates';

const messages = defineMessages({
  favoriteTemplate: {
    id: 'FavoriteTemplateButton.favoriteTemplate',
    defaultMessage: 'Favorite template',
  },
  unfavoriteTemplate: {
    id: 'FavoriteTemplateButton.unfavoriteTemplate',
    defaultMessage: 'Remove favorite template',
  },
});

const useStyles = makeStyles(theme => ({
  favoriteTemplateButton: {
    padding: 0,
  },
  favorited: {
    '& svg': {
      stroke: theme.palette.red.main,
    },
    '& svg path': {
      fill: theme.palette.red.main,
    },
  },
}));

type FavoriteTemplateButtonProps = {
  template: TemplateSummary;
  className?: string;
};

export const FavoriteTemplateButton: React.FC<FavoriteTemplateButtonProps> = ({
  template,
  className,
}) => {
  const { formatMessage } = useIntl();
  const styles = useStyles();
  const { isFavoriteTemplate, toggleFavoriteTemplate } =
    useFavoriteTemplatingTemplates();
  const isFavorite = isFavoriteTemplate(template);

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();
      toggleFavoriteTemplate(template);
    },
    [template, toggleFavoriteTemplate],
  );

  return (
    <IconButton
      size={0}
      icon={Heart}
      aria-label={formatMessage(
        isFavorite ? messages.unfavoriteTemplate : messages.favoriteTemplate,
      )}
      onClick={handleClick}
      className={clsx(
        className,
        styles.favoriteTemplateButton,
        isFavorite && styles.favorited,
      )}
      color="inverse"
    />
  );
};
